import { createSlice } from '@reduxjs/toolkit';
import v4 from 'uuid/v4';

/**
 * Nerobotti conversation state.
 *
 * Replaces `scenes/Settings/Chatbot/store/slices/chatbotSlice.js`, which is
 * deprecated along with the rest of the old bot. Three things are deliberately
 * gone:
 *
 *  - `isAdmin` / `isBulletin`. There are no modes any more. The assistant picks
 *    tools based on the question; the user does not pick a data source.
 *  - Everything scope-shaped. The old client sent `businessId`, `projectKey`
 *    and `admin` with every message and the backend believed them, which is how
 *    any authenticated user could read any tenant's documents. The client now
 *    sends the message and nothing that grants anything.
 *  - Silent failure. The old slice had an `evt.type === 'error'` branch that
 *    nothing ever emitted, so a backend failure left `isLoading` true forever
 *    with the send button disabled until reload. Every terminal path here
 *    clears it.
 */

const initialState = {
    isOpen: false,
    isFullscreen: false,
    /**
     * Turns, in order. Two shapes:
     *
     *  - `{ id, role: 'user' | 'assistant', content, status, timestamp }`
     *  - `{ id, role: 'action', actionId, summary, expiresAt, state, outcome,
     *     link, timestamp }` — a change the assistant offered, awaiting a tap.
     *    `link` is `{ url, label }` and arrives only once the change succeeded.
     *
     * The action item is the only interactive thing in the stream, and it is
     * deliberately in the stream rather than in a modal: what is being agreed to
     * belongs next to the sentence that proposed it.
     */
    discussion: [],
    /** Groups turns into one conversation so the assistant can follow context. */
    threadId: null,
    isAwaitingReply: false,
    error: null,
    connectionState: 'idle', // idle | connecting | open | closed
};

export const nerobottiSlice = createSlice({
    name: 'nerobotti',
    initialState,
    reducers: {
        openNerobotti: (state) => {
            state.isOpen = true;
        },

        closeNerobotti: (state) => {
            state.isOpen = false;
        },

        toggleNerobotti: (state) => {
            state.isOpen = !state.isOpen;
        },

        toggleFullscreen: (state) => {
            state.isFullscreen = !state.isFullscreen;
        },

        /** Wipe the conversation. A new threadId means a fresh context. */
        startNewConversation: (state) => {
            state.discussion = [];
            state.threadId = null;
            state.isAwaitingReply = false;
            state.error = null;
        },

        setConnectionState: (state, action) => {
            state.connectionState = action.payload;

            if (action.payload !== 'closed') return;

            // A socket that closed while we were waiting is a dead turn. Say so
            // rather than leaving a placeholder spinning.
            if (state.isAwaitingReply) {
                state.isAwaitingReply = false;
                failLastAssistantTurn(
                    state,
                    'Yhteys katkesi. Yritä uudelleen.'
                );
            }

            // A card mid-confirm is the same problem with worse consequences:
            // the write may or may not have happened, and we no longer have a
            // socket to be told which. Say exactly that rather than implying
            // either.
            //
            // A card still in 'pending' is deliberately left alone. The offer
            // lives server-side keyed by user, not by connection, so it survives
            // a reconnect and stays tappable.
            state.discussion.forEach((turn) => {
                if (turn.role === 'action' && turn.state === 'confirming') {
                    turn.state = 'failed';
                    turn.outcome =
                        'Yhteys katkesi. Tarkista tiedotetaulu ennen kuin yrität uudelleen.';
                }
            });
        },

        /** Optimistic: the user's turn plus the placeholder deltas fill in. */
        sendMessage: (state, action) => {
            const { text, threadId } = action.payload;

            // The caller mints the id and passes it in, rather than this
            // reducer inventing one. Minting it here looks tidier and is a
            // trap: `dispatch` does not re-render synchronously, so the panel
            // that just dispatched still reads `null` and sends the message
            // unthreaded. The server then drops the turn on the floor —
            // `saveTurn` requires a thread id — and the first exchange of every
            // conversation is invisible to the next one.
            if (!state.threadId) {
                state.threadId = threadId ?? `${new Date().toISOString()}#${v4()}`;
            }

            state.error = null;
            state.isAwaitingReply = true;

            state.discussion.push({
                id: v4(),
                role: 'user',
                content: text,
                status: 'final',
                timestamp: Date.now(),
            });

            state.discussion.push({
                id: v4(),
                role: 'assistant',
                content: '',
                status: 'streaming',
                timestamp: Date.now(),
            });
        },

        /** A frame from the socket: delta | done | error | confirm | pong. */
        receiveSocketEvent: (state, action) => {
            const event = action.payload;
            const last = lastAssistantTurn(state);

            switch (event?.type) {
                case 'delta': {
                    // The old slice did `last.content += ...` unguarded, which
                    // throws if a delta arrives with no placeholder (a late
                    // frame from an abandoned turn, for instance).
                    if (last && typeof event.text === 'string') {
                        last.content += event.text;
                    }
                    break;
                }

                case 'done': {
                    // A `done` carrying an actionId is the outcome of a
                    // confirmed action, not the end of a reply. It must not
                    // touch the last assistant turn: no placeholder was created
                    // for a button press, so `last` is a finished message from
                    // earlier and writing to it would overwrite real text.
                    if (event.actionId) {
                        resolveActionTurn(state, event);
                        state.isAwaitingReply = false;
                        break;
                    }

                    if (last) {
                        // Prefer the authoritative full text over accumulated
                        // deltas — a dropped frame would otherwise leave a gap.
                        if (typeof event.text === 'string' && event.text) {
                            last.content = event.text;
                        }
                        last.status = 'final';
                    }
                    state.isAwaitingReply = false;
                    break;
                }

                /**
                 * The assistant is offering to change something. Arrives after
                 * `done`, so the reply bubble is already final and the composer
                 * already unlocked — the card lands underneath a finished
                 * message.
                 */
                case 'confirm': {
                    if (!event.actionId) break;

                    state.discussion.push({
                        id: v4(),
                        role: 'action',
                        actionId: event.actionId,
                        // The server's sentence, not the model's prose. This is
                        // the thing being agreed to.
                        summary: event.summary || '',
                        expiresAt: event.expiresAt ?? null,
                        state: 'pending',
                        outcome: null,
                        link: null,
                        timestamp: Date.now(),
                    });
                    break;
                }

                case 'error': {
                    state.isAwaitingReply = false;
                    state.error = event.message || 'Jokin meni vikaan.';

                    // Same split as `done`: an error about an action belongs on
                    // its card, not appended to an unrelated message.
                    if (event.actionId) {
                        resolveActionTurn(state, {
                            ...event,
                            text: state.error,
                            status: 'error',
                        });
                        break;
                    }

                    failLastAssistantTurn(state, state.error);
                    break;
                }

                default:
                    break;
            }
        },

        /**
         * The user tapped Vahvista. Optimistic only in appearance — nothing is
         * written until the server says so, and the server re-checks permissions
         * before it does.
         */
        confirmAction: (state, action) => {
            const turn = findActionTurn(state, action.payload?.actionId);
            if (!turn || turn.state !== 'pending') return;

            state.error = null;
            turn.state = 'confirming';
            state.isAwaitingReply = true;
        },

        /** The user dismissed the card. Client-side only; the offer just expires. */
        dismissAction: (state, action) => {
            const turn = findActionTurn(state, action.payload?.actionId);
            if (!turn || turn.state !== 'pending') return;

            turn.state = 'dismissed';
        },

        /** The confirm frame never left the client. */
        confirmFailed: (state, action) => {
            const { actionId, message } = action.payload ?? {};
            const turn = findActionTurn(state, actionId);

            state.isAwaitingReply = false;

            if (!turn) return;

            turn.state = 'failed';
            turn.outcome = message || 'Vahvistuksen lähetys epäonnistui.';
        },

        /** The socket refused the message outright (never reached the server). */
        sendFailed: (state, action) => {
            state.isAwaitingReply = false;
            state.error = action.payload || 'Viestin lähetys epäonnistui.';
            failLastAssistantTurn(state, state.error);
        },

        dismissError: (state) => {
            state.error = null;
        },
    },
});

/**
 * The streaming placeholder, if there is one.
 *
 * Note this looks only at the *last* item, so a card sitting at the end of the
 * discussion correctly yields null — a stray delta after an offer has nowhere to
 * go rather than landing on the wrong turn.
 */
const lastAssistantTurn = (state) => {
    const last = state.discussion[state.discussion.length - 1];
    return last && last.role === 'assistant' ? last : null;
};

const findActionTurn = (state, actionId) =>
    actionId
        ? state.discussion.find(
              (turn) => turn.role === 'action' && turn.actionId === actionId
          )
        : undefined;

/**
 * Record what became of a confirmed action.
 *
 * Keyed by actionId rather than by position: the outcome arrives on its own
 * invocation and the user may have sent other messages in between, so the card
 * is not necessarily the last thing in the stream.
 */
const resolveActionTurn = (state, event) => {
    const turn = findActionTurn(state, event.actionId);
    if (!turn) return;

    // Never downgrade a card that already succeeded. A second tap is refused by
    // the server with "already done", which is a true statement but arrives
    // without an ok status — treating it as a failure would turn a bulletin that
    // was genuinely posted red.
    if (turn.state === 'done') return;

    turn.state = event.status === 'ok' ? 'done' : 'failed';
    turn.outcome = event.text || null;
    // Built by the server from ids it already holds — never parsed out of the
    // assistant's prose, for the same reason `summary` isn't.
    turn.link = event.link ?? null;
};

/**
 * Turn a stuck placeholder into a visible failure. Without this the user sees
 * an empty bubble and a spinner that never resolves — the single worst
 * behaviour of the bot this replaces.
 */
const failLastAssistantTurn = (state, message) => {
    const last = lastAssistantTurn(state);
    if (!last || last.status === 'final') return;

    last.status = 'error';
    last.content = last.content
        ? `${last.content}\n\n_${message}_`
        : `_${message}_`;
};

export const {
    openNerobotti,
    closeNerobotti,
    toggleNerobotti,
    toggleFullscreen,
    startNewConversation,
    setConnectionState,
    sendMessage,
    receiveSocketEvent,
    sendFailed,
    dismissError,
    confirmAction,
    dismissAction,
    confirmFailed,
} = nerobottiSlice.actions;

// SELECTORS
export const selectNerobotti = (state) => state.nerobottiSlice ?? initialState;
export const selectIsNerobottiOpen = (state) => selectNerobotti(state).isOpen;
export const selectDiscussion = (state) => selectNerobotti(state).discussion;
export const selectIsAwaitingReply = (state) =>
    selectNerobotti(state).isAwaitingReply;
export const selectThreadId = (state) => selectNerobotti(state).threadId;
export const selectNerobottiError = (state) => selectNerobotti(state).error;
